import React from 'react';
import Modal from './Modal';
import Button from './Button';
import { TicketsSingleView } from '../hooks/useTicketsSingleView';
import { PurchasedTickets } from '../services/ticketService';
import { formatEventDate, sameEventDay } from '../utils/dates';

interface TicketsModalProps {
    isOpen: boolean;
    onClose: () => void;
    tickets: TicketsSingleView;
}

// Event wall times, shown as the organizer typed them
const formatEventDates = (start?: string, end?: string) => {
    if (!start) return '';
    const date = formatEventDate(start, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const time = (value: string) => formatEventDate(value, { hour: 'numeric', minute: '2-digit' });
    if (!end) return `${date}, ${time(start)}`;
    return sameEventDay(start, end) ? `${date}, ${time(start)} a ${time(end)}` : `${date}, ${time(start)}`;
};

const Spinner = ({ text }: { text: string }) => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="st-spinner st-spinner-xl mb-4"></div>
        <p className="text-[var(--st-text-secondary)] font-medium">{text}</p>
    </div>
);

const TicketList = ({ purchase }: { purchase: PurchasedTickets }) => {
    const eventDate = formatEventDates(purchase.eventStart, purchase.eventEnd);
    return (
        <div>
            <div className="st-alert st-alert-warning mb-5">
                <div className="st-alert-content">
                    <p className="st-alert-title">Por seguridad, tus boletas se muestran una sola vez</p>
                    <p className="st-alert-message">
                        Si recargas o sales de esta página no podrás volver a verlas aquí. También te las enviamos en PDF
                        a <span className="font-semibold">{purchase.buyerEmail}</span>.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                {purchase.tickets.map((ticket, index) => (
                    <div
                        key={ticket.code}
                        className="flex flex-col sm:flex-row items-center gap-5 rounded-xl border border-[var(--st-border)] p-4"
                    >
                        <img
                            src={ticket.qrImage}
                            alt={`Código QR de la boleta ${ticket.code}`}
                            className="w-48 h-48 flex-shrink-0 bg-white"
                        />
                        <div className="text-center sm:text-left">
                            <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider">
                                Boleta {index + 1} de {purchase.tickets.length}
                            </p>
                            <p className="font-display text-xl font-bold text-[var(--st-accent-primary)]">{ticket.stageName}</p>
                            <p className="font-mono font-bold text-[var(--st-text-primary)] tracking-wider mb-2">{ticket.code}</p>
                            <p className="font-bold text-[var(--st-text-primary)]">{purchase.eventName}</p>
                            {eventDate && <p className="text-sm text-[var(--st-text-secondary)] first-letter:uppercase">{eventDate}</p>}
                            {purchase.eventLocation && <p className="text-sm text-[var(--st-text-secondary)]">{purchase.eventLocation}</p>}
                        </div>
                    </div>
                ))}
            </div>

            <p className="text-xs text-[var(--st-text-muted)] mt-5">
                Presenta el código QR de cada boleta en la entrada. Es único y la primera lectura lo invalida: no lo compartas.
            </p>
        </div>
    );
};

const ResendBlock = ({ tickets }: { tickets: TicketsSingleView }) => {
    const { resendState, resend } = tickets;
    return (
        <div className="mt-5">
            {resendState === 'sent' && (
                <div className="st-alert st-alert-success mb-4">
                    <div className="st-alert-content">
                        <p className="st-alert-message">
                            Listo. Te reenviamos las boletas al correo de tu compra; revisa también la carpeta de spam.
                        </p>
                    </div>
                </div>
            )}
            {resendState === 'limit' && (
                <div className="st-alert st-alert-warning mb-4">
                    <div className="st-alert-content">
                        <p className="st-alert-message">Ya te las reenviamos varias veces. Intenta de nuevo en una hora.</p>
                    </div>
                </div>
            )}
            {resendState === 'error' && (
                <div className="st-alert st-alert-error mb-4">
                    <div className="st-alert-content">
                        <p className="st-alert-message">No pudimos reenviarlas. Intenta de nuevo en unos segundos.</p>
                    </div>
                </div>
            )}
            {resendState !== 'sent' && resendState !== 'limit' && (
                <Button
                    className="st-btn st-btn-primary w-full"
                    onClick={resend}
                    disabled={resendState === 'sending'}
                >
                    {resendState === 'sending' ? 'Reenviando...' : 'Reenviar a mi correo'}
                </Button>
            )}
        </div>
    );
};

const Message = ({ title, text }: { title: string; text: React.ReactNode }) => (
    <div className="text-center py-4">
        <h4 className="font-display text-lg font-bold text-[var(--st-text-primary)] mb-2">{title}</h4>
        <p className="text-[var(--st-text-secondary)]">{text}</p>
    </div>
);

const TicketsModal: React.FC<TicketsModalProps> = ({ isOpen, onClose, tickets }) => {
    const { view, retry } = tickets;

    const content = () => {
        switch (view.kind) {
            case 'idle':
            case 'loading':
                return <Spinner text="Cargando tus boletas..." />;
            case 'generating':
                return <Spinner text="Estamos generando tus boletas..." />;
            case 'delayed':
                return (
                    <>
                        <Message
                            title="Tus boletas aún se están generando"
                            text="Te llegarán en PDF al correo de tu compra en cuanto estén listas. También puedes intentar verlas de nuevo en un momento."
                        />
                        <Button className="st-btn st-btn-primary w-full mt-4" onClick={retry}>Intentar de Nuevo</Button>
                    </>
                );
            case 'shown':
                return <TicketList purchase={view.tickets} />;
            case 'viewed':
                return (
                    <>
                        <Message
                            title="Tus boletas ya se mostraron"
                            text={<>Por seguridad solo se muestran una vez en esta página. Te las enviamos en PDF a tu
                                correo{view.maskedEmail ? <> (<span className="font-semibold">{view.maskedEmail}</span>)</> : ''}.</>}
                        />
                        <ResendBlock tickets={tickets} />
                    </>
                );
            case 'unavailable':
                return (
                    <>
                        <Message
                            title="Tus boletas están en tu correo"
                            text="Por seguridad, solo se pueden ver aquí en el navegador donde hiciste la compra. Te las enviamos en PDF al correo de tu compra."
                        />
                        <ResendBlock tickets={tickets} />
                    </>
                );
            case 'error':
                return (
                    <>
                        <Message
                            title="No pudimos cargar tus boletas"
                            text="Revisa tu conexión e intenta de nuevo. Tus boletas también llegan en PDF al correo de tu compra."
                        />
                        <Button className="st-btn st-btn-primary w-full mt-4" onClick={retry}>Intentar de Nuevo</Button>
                    </>
                );
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Mis boletas" className="max-w-xl">
            {content()}
        </Modal>
    );
};

export default TicketsModal;
